import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DashboardAnalytics } from "../../types/dashboard.types";

/**
 * Dashboard Analytics Slice
 *
 * Manages analytics data for the Reports & Analytics page.
 * Separated from dashboardSlice for better organization and performance.
 *
 * Data is cached in Redux and only fetched when:
 * - User navigates to Reports & Analytics page for the first time
 * - User clicks the refresh button
 * - Page is reloaded
 */

interface DashboardAnalyticsState {
  analytics: DashboardAnalytics | null;
  loading: boolean;
  error: string | null;
  lastFetched: number | null;
  timeRange: string; // Current selected time range filter
}

const initialState: DashboardAnalyticsState = {
  analytics: null,
  loading: false,
  error: null,
  lastFetched: null,
  timeRange: "all", // Default to "all"
};

const dashboardAnalyticsSlice = createSlice({
  name: "dashboardAnalytics",
  initialState,
  reducers: {
    /**
     * Fetch analytics request
     */
    fetchAnalyticsRequest: (
      state,
      _action: PayloadAction<{
        facilityId: string | number;
        timePeriod?: string;
      }>
    ) => {
      state.loading = true;
      state.error = null;
    },

    /**
     * Fetch analytics success
     */
    fetchAnalyticsSuccess: (
      state,
      action: PayloadAction<{ analytics: DashboardAnalytics }>
    ) => {
      state.analytics = action.payload.analytics;
      state.loading = false;
      state.error = null;
      state.lastFetched = Date.now();
    },

    /**
     * Fetch analytics failure
     */
    fetchAnalyticsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    /**
     * Clear analytics data (useful for logout or manual reset)
     */
    clearAnalytics: (state) => {
      state.analytics = null;
      state.loading = false;
      state.error = null;
      state.lastFetched = null;
      state.timeRange = "all";
    },

    /**
     * Set time range filter
     */
    setAnalyticsTimeRange: (state, action: PayloadAction<string>) => {
      state.timeRange = action.payload;
    },
  },
});

export const {
  fetchAnalyticsRequest,
  fetchAnalyticsSuccess,
  fetchAnalyticsFailure,
  clearAnalytics,
  setAnalyticsTimeRange,
} = dashboardAnalyticsSlice.actions;

export default dashboardAnalyticsSlice.reducer;
